import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  getConsentSnapshot,
  hasCookieDecision,
  onCookieSettingsRequested,
  saveCookieConsent,
} from "../utils/cookieConsent";
import { adsConfig } from "../analytics/config";

// Only show the ads category if ads are actually enabled in this build.
const ADS_ENABLED = Boolean(adsConfig.enabled);

function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [hasDecision, setHasDecision] = useState(false);
  const [form, setForm] = useState({ analytics: false, ads: false, preferences: false });

  useEffect(() => {
    const consent = getConsentSnapshot();
    const decided = hasCookieDecision();

    setHasDecision(decided);
    setForm({
      analytics: Boolean(consent.analytics),
      ads: Boolean(consent.ads),
      preferences: Boolean(consent.preferences),
    });
    if (!decided) setVisible(true);

    const unsubscribe = onCookieSettingsRequested(() => {
      const current = getConsentSnapshot();
      setHasDecision(hasCookieDecision());
      setForm({
        analytics: Boolean(current.analytics),
        ads: Boolean(current.ads),
        preferences: Boolean(current.preferences),
      });
      setCustomizing(true);
      setVisible(true);
    });

    return unsubscribe;
  }, []);

  const applyDecision = (next) => {
    saveCookieConsent(next);
    setHasDecision(true);
    setVisible(false);
    setCustomizing(false);
  };

  const handleAcceptAll = () => {
    applyDecision({ analytics: true, ads: true, preferences: true, source: "accept_all" });
  };

  const handleRejectNonEssential = () => {
    applyDecision({ analytics: false, ads: false, preferences: false, source: "reject_non_essential" });
  };

  const handleSaveCustom = () => {
    applyDecision({ ...form, source: "custom" });
  };

  const handleCancelCustomize = () => {
    if (hasDecision) {
      setVisible(false);
      setCustomizing(false);
      return;
    }
    setCustomizing(false);
  };

  if (!visible) return null;

  return (
    <div className="cookie-banner" role="dialog" aria-live="polite" aria-label="Preferencias de cookies">
      <div className="cookie-banner__content">
        <p>
          Usamos cookies necesarias para el funcionamiento de XPHub y, si lo permites, cookies de analítica
          {ADS_ENABLED ? " y publicidad" : ""} para medir{ADS_ENABLED ? " y financiar" : ""} el servicio. Consulta la{" "}
          <Link to="/privacy" className="cookie-link">Política de Privacidad</Link> y la{" "}
          <Link to="/cookies" className="cookie-link">Política de Cookies</Link>.
        </p>

        {!customizing ? (
          <div className="cookie-buttons">
            <button onClick={handleAcceptAll} className="cookie-accept">Aceptar todo</button>
            <button onClick={handleRejectNonEssential} className="cookie-reject">Rechazar no esenciales</button>
            <button onClick={() => setCustomizing(true)} className="cookie-manage">Personalizar</button>
          </div>
        ) : (
          <div className="cookie-customize">
            <label className="cookie-option cookie-option--fixed">
              <span>
                <strong>Necesarias</strong>
                <small>Siempre activas para login, sesión y seguridad.</small>
              </span>
              <input type="checkbox" checked disabled aria-label="Cookies necesarias siempre activas" />
            </label>

            <label className="cookie-option">
              <span>
                <strong>Analíticas</strong>
                <small>Medición de uso para mejorar rendimiento y producto.</small>
              </span>
              <input
                type="checkbox"
                checked={form.analytics}
                onChange={(e) => setForm((prev) => ({ ...prev, analytics: e.target.checked }))}
                aria-label="Activar cookies analíticas"
              />
            </label>

            {ADS_ENABLED && (
              <label className="cookie-option">
                <span>
                  <strong>Publicidad</strong>
                  <small>Habilita espacios publicitarios no intrusivos y medición de anuncios.</small>
                </span>
                <input
                  type="checkbox"
                  checked={form.ads}
                  onChange={(e) => setForm((prev) => ({ ...prev, ads: e.target.checked }))}
                  aria-label="Activar cookies publicitarias"
                />
              </label>
            )}

            <label className="cookie-option">
              <span>
                <strong>Preferencias</strong>
                <small>Guardar ajustes de interfaz no críticos.</small>
              </span>
              <input
                type="checkbox"
                checked={form.preferences}
                onChange={(e) => setForm((prev) => ({ ...prev, preferences: e.target.checked }))}
                aria-label="Activar cookies de preferencias"
              />
            </label>

            <div className="cookie-buttons">
              <button onClick={handleSaveCustom} className="cookie-accept">Guardar selección</button>
              <button onClick={handleCancelCustomize} className="cookie-manage">Cancelar</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CookieBanner;
